import type { RankQuestionAnswerDefinitionOptionDto } from '@/api/generated';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';

import {
  DndContext,
  MouseSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Dispatch, FC, SetStateAction } from 'react';
import { ListGroup } from 'react-bootstrap';

import { SortableItem } from './SortableItem';

interface Props {
  items: RankQuestionAnswerDefinitionOptionDto[];
  setItems: Dispatch<SetStateAction<RankQuestionAnswerDefinitionOptionDto[]>>;
}

const DraggableList: FC<Props> = ({ items, setItems }) => {
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems(prevItems => {
        const oldIndex = prevItems.findIndex(item => item.index === parseInt(active.id as string));
        const newIndex = prevItems.findIndex(item => item.index === parseInt(over.id as string));

        const newItems = arrayMove(prevItems, oldIndex, newIndex);

        return newItems.map((item, idx) => ({
          ...item,
          order: idx,
        }));
      });
    }
  };

  return (
    <DndContext
      autoScroll={{ acceleration: 100, layoutShiftCompensation: false }}
      collisionDetection={rectIntersection}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext
        items={items.map(item => item.index.toString())}
        strategy={verticalListSortingStrategy}
      >
        <ListGroup className="gap-s py-3 pe-12 p-md-0">
          {items
            .toSorted((a, b) => a.order - b.order)
            .map(item => (
              <SortableItem answer={item.answer} id={item.index.toString()} key={item.index} />
            ))}
        </ListGroup>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableList;
